import {
  Box,
  HelperField,
  ModalBody,
  ModalFoot,
  ModalHead,
  Select,
  SelectItem,
} from '@dixa/lib-shared-habla';
import { boundMethod } from 'autobind-decorator';
import { Template } from 'lib-shared-legacy-types';
import { Component } from 'react';
import { v4 as uuid } from 'uuid';

import { getConfirmButtonVerb } from 'components/powerFlowShared/helpers/getConfirmButtonVerb';
import type { FlowManagerEditModalComponentT } from 'fable/flowEditor';
import { AutomationNodeTypes, withTemplates } from 'hocs';
import type { WithTemplatesInjectedProps } from 'hocs';
import { durationConverter } from 'utils/duration';

import {
  getAutoReplyValidity,
  MIN_WAIT_TIME_BETWEEN_REPLIES,
} from '../automationFlow-utils';
import type { AutomationNodeReplyT } from '../nodeTypes';
type PropsT = FlowManagerEditModalComponentT<AutomationNodeReplyT> &
  WithTemplatesInjectedProps<TemplateWithIdT>;

type StateT = {
  templateId: string | null;
  templateName: string;
  validation: ValidationT;
};

type ValidationT = {
  template?: string;
};

type TemplateWithIdT = Template & {
  id: string;
};

class ReplyModalImp extends Component<PropsT, StateT> {
  constructor(props: PropsT) {
    super(props);

    const { node } = props;
    this.state = {
      templateId: node.payload?.templateId || null,
      templateName: node.payload?.templateName || '',
      validation: {},
    };
  }

  componentDidMount() {
    const { templates } = this.props;
    return templates.getItems();
  }

  @boundMethod
  handleConfirm(): void {
    const errors = this.validate();
    if (Object.keys(errors).length > 0) {
      this.setState({ validation: errors });
      return;
    }

    this.saveNode();
  }

  saveNode() {
    const { replaceNode, node } = this.props;
    const { templateId, templateName } = this.state;
    const nodeOptions: AutomationNodeReplyT = {
      ...node,
      nodeType: AutomationNodeTypes.REPLY,
      isAction: false,
      isRemovable: true,
      isEditable: true,
      payload: {
        templateId: templateId as string, // validated by handleConfirm
        templateName: templateName as string, // validated by handleConfirm
        nextNodeId: node.payload?.nextNodeId || uuid(),
      },
    };

    replaceNode(nodeOptions);
    this.closeModal();
  }

  @boundMethod
  closeModal() {
    this.props.triggerClose();
  }

  getTemplateOptions() {
    return this.props.templates.items.map((t) => {
      return { id: t.id, name: t.name };
    });
  }

  getPagination() {
    const { templates } = this.props;
    return {
      nextEnable: templates.hasNextPage,
      onNext: templates.nextPage,
      previousEnable: templates.hasPreviousPage,
      onPrevious: templates.previousPage,
    };
  }

  @boundMethod
  onTemplateSearch(searchTerm: string | undefined) {
    const { templates } = this.props;
    if (searchTerm !== undefined && searchTerm !== templates.params.search) {
      templates.paramsChange({ search: searchTerm });
    }
  }

  @boundMethod
  onTemplateSelect(template: TemplateWithIdT) {
    this.setState({
      templateId: template.id,
      templateName: template.name,
      validation: {},
    });
  }

  validate() {
    const errors: ValidationT = {};
    const { templateId } = this.state;
    if (!templateId) {
      errors.template = 'Please select a template';
    }

    return errors;
  }

  render() {
    const { templateId, templateName, validation } = this.state;
    const { nodes, node, nodeLabel } = this.props;
    const { payload } = node;
    const spamProtection = getAutoReplyValidity(nodes)(node);

    const isChanged = payload?.templateId !== templateId;

    return (
      <Box width="600px">
        <ModalHead title={nodeLabel} onClose={this.closeModal}>
          Send an automatic reply based on a custom template.
          <br />
          Auto-reply templates can be created under{' '}
          <strong>Settings &gt; Templates</strong>.
        </ModalHead>
        <ModalBody>
          <Box>
            <HelperField
              fieldId="template"
              label="Template"
              errorMessage={validation.template}
              required
            >
              <></>
            </HelperField>

            <Select
              itemComponent={SelectItem}
              items={this.getTemplateOptions()}
              pagination={this.getPagination()}
              onSearch={this.onTemplateSearch}
              onSelect={this.onTemplateSelect}
              selectedItems={[
                { name: templateName, id: templateId ?? templateName },
              ]}
            ></Select>
          </Box>

          <Box
            background="blue.100"
            borderRadius
            margin="500 0 0 0"
            padding="300"
            color="neutral.900"
          >
            As a protective measure against spam, another auto-reply can only be
            sent after{' '}
            {durationConverter.toString(MIN_WAIT_TIME_BETWEEN_REPLIES)} have
            passed.
            {spamProtection.isInvalid &&
              typeof spamProtection.waitTime === 'number' && (
                <>
                  <br />
                  <br />
                  You have{' '}
                  <strong>
                    {durationConverter.toString(spamProtection.waitTime) ||
                      'no wait time'}
                  </strong>{' '}
                  between this and the previous auto-reply.
                </>
              )}
          </Box>
        </ModalBody>
        <ModalFoot
          confirmOnClick={this.handleConfirm}
          confirmName={getConfirmButtonVerb(node)}
          confirmProps={{ disabled: !isChanged }}
          cancelOnClick={this.closeModal}
        />
      </Box>
    );
  }
}

export const ReplyModal = withTemplates<PropsT, TemplateWithIdT>(
  ReplyModalImp,
  {
    initialParams: { type: 'email_auto_reply' },
    itemNormalizer: (item: Template): TemplateWithIdT => ({
      ...item,
      id: (item as TemplateWithIdT).id ?? item.name,
    }),
  },
);
